import React, { useEffect, useState } from "react";
import ReactTable from "react-table";
import { Checkbox, FormControlLabel, IconButton, TextField, Tooltip } from "@material-ui/core";
import { Description, Refresh, Visibility } from "@material-ui/icons";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import Button from "../../components/CustomButtons/Button";
import CustomPDFViewer from "../../components/CustomPDFViewer/CustomPDFViewer";
import axiosGci from "store/axios/axios-gci";
import axiosPr from "store/axios/axios-pr";
import { formatUTCtoPST, handleError } from "../../shared/utility";
import { renderGeneratedPaymentReportLegacyHTML } from "../PaymentReports/generatedPaymentReportLegacyHTML";
import {
    getPaymentReportGenerationError,
    getSimulationValidationError,
    paymentReportPdfRequestConfig,
    paymentReportRequestConfig
} from "../PaymentReports/paymentReportGeneration";

const stripNonDigits = value => String(value || "").split("").filter(char => char >= "0" && char <= "9").join("");

const currentYearStart = () => `${new Date().getFullYear()}-01-01`;

const LoadingIndicator = ({ label = "Loading", large = false }) => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: large ? 12 : 8,
            color: large ? "#333" : "#666",
            fontSize: large ? 18 : 14,
            fontWeight: large ? 600 : 400,
            lineHeight: 1.35
        }}
    >
        <style>{"@keyframes gci-loading-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
        <Refresh style={{ animation: "gci-loading-spin 0.9s linear infinite", fontSize: large ? 34 : 20 }} />
        <span>{label}</span>
    </span>
);

const TableLoading = ({ loading, loadingText }) => loading ? (
    <div
        style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: 34,
            background: "rgba(255,255,255,0.82)"
        }}
    >
        <div
            style={{
                padding: "14px 22px",
                borderRadius: 6,
                background: "#fff",
                boxShadow: "0 3px 14px rgba(0,0,0,0.14)",
                border: "1px solid #eee"
            }}
        >
            <LoadingIndicator label={loadingText || "Loading"} large />
        </div>
    </div>
) : null;

const money = value => {
    if (value == null || value === "") return "";
    const parsed = Number(String(value).replace("$", "").split(",").join(""));
    if (Number.isNaN(parsed)) return value;
    return parsed.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const dateOnly = value => {
    if (!value) return "";
    if (String(value).indexOf("T") !== -1) return String(value).split("T")[0];
    return String(value).split(" ")[0];
};

const GCIPaymentReports = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [workingPaymentId, setWorkingPaymentId] = useState(null);
    const [workingMessage, setWorkingMessage] = useState("");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [paymentIdFilter, setPaymentIdFilter] = useState("");
    const [invoiceNumberFilter, setInvoiceNumberFilter] = useState("");
    const [rsIdFilter, setRsIdFilter] = useState("");
    const [startDateFilter, setStartDateFilter] = useState(currentYearStart());
    const [endDateFilter, setEndDateFilter] = useState("");
    const [sortBy, setSortBy] = useState("payment_created_date");
    const [sortDirection, setSortDirection] = useState("DESC");
    const [onlyMissingReports, setOnlyMissingReports] = useState(false);
    const [pdfData, setPdfData] = useState(null);

    const loadPayments = async (options = {}) => {
        const nextPage = options.page !== undefined ? options.page : page;
        const nextPageSize = options.pageSize !== undefined ? options.pageSize : pageSize;
        const nextOnlyMissingReports = options.onlyMissingReports !== undefined
            ? options.onlyMissingReports
            : onlyMissingReports;
        const nextPaymentId = options.paymentId !== undefined ? options.paymentId : paymentIdFilter;
        const nextInvoiceNumber = options.invoiceNumber !== undefined ? options.invoiceNumber : invoiceNumberFilter;
        const nextRsId = options.rsId !== undefined ? options.rsId : rsIdFilter;
        const nextStartDate = options.startDate !== undefined ? options.startDate : startDateFilter;
        const nextEndDate = options.endDate !== undefined ? options.endDate : endDateFilter;
        const nextSortBy = options.sortBy !== undefined ? options.sortBy : sortBy;
        const nextSortDirection = options.sortDirection !== undefined ? options.sortDirection : sortDirection;

        try {
            setLoading(true);
            const params = {
                page: nextPage,
                pageSize: nextPageSize,
                only_missing_reports: nextOnlyMissingReports ? 1 : 0,
                sort_by: nextSortBy,
                sort_dir: nextSortDirection
            };
            if (nextPaymentId) params.payment_id = nextPaymentId;
            if (nextInvoiceNumber) params.invoice_number = nextInvoiceNumber;
            if (nextRsId) params.rs_id = nextRsId;
            if (nextStartDate) params.start_date = nextStartDate;
            if (nextEndDate) params.end_date = nextEndDate;

            const response = await axiosGci.get("/payment-report-payments", { params });
            setPayments(response.data.data || []);
            setTotal(response.data.total || 0);
            setPage(response.data.page || 0);
            setPageSize(response.data.pageSize || nextPageSize);
        } catch (error) {
            handleError(error);
            alert("Could not load payments.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPayments({ page: 0, startDate: currentYearStart() });
    }, []);

    const createPdf = async htmlContent => {
        const response = await axiosPr.post("/pdf", { htmlContent }, paymentReportPdfRequestConfig);
        return response.data;
    };

    const openLatestSavedReport = async payment => {
        if (!payment.latest_report_id) return;

        try {
            setWorkingPaymentId(payment.id);
            setWorkingMessage("Opening report and creating PDF");
            const response = await axiosPr.get(`/payment_reports2/${payment.latest_report_id}`);
            const htmlContent = response.data && response.data.data ? response.data.data.html_content : "";
            if (!htmlContent) {
                alert("The latest saved report does not have saved HTML.");
                return;
            }
            setPdfData(await createPdf(htmlContent));
        } catch (error) {
            handleError(error);
            alert("Could not open the saved payment report.");
        } finally {
            setWorkingPaymentId(null);
            setWorkingMessage("");
        }
    };

    const generatePaymentReport = async payment => {
        try {
            setWorkingPaymentId(payment.id);
            setWorkingMessage("Generating report data");
            const reportResponse = await axiosPr.get(
                `/payment_report_preview?payment_id=${payment.id}&require_simulation=1`,
                paymentReportRequestConfig
            );
            const simulationValidationError = getSimulationValidationError(reportResponse.data);
            if (simulationValidationError) throw new Error(simulationValidationError);
            setWorkingMessage("Rendering report HTML");
            const htmlContent = renderGeneratedPaymentReportLegacyHTML(reportResponse.data);
            setWorkingMessage("Saving generated report");
            const createResponse = await axiosPr.post("/payment_reports2", {
                payment_id: payment.id,
                html_content: htmlContent,
                data: {
                    report: reportResponse.data,
                    payment_id: payment.id,
                    invoice_id: payment.invoice_id,
                    rs_id: payment.rs_id
                }
            }, paymentReportRequestConfig);
            setWorkingMessage("Refreshing payments list");
            await loadPayments({ page });
            setWorkingMessage("Creating PDF");
            setPdfData(await createPdf(htmlContent));
            return createResponse.data;
        } catch (error) {
            handleError(error);
            alert(getPaymentReportGenerationError(error));
        } finally {
            setWorkingPaymentId(null);
            setWorkingMessage("");
        }
    };

    const resetFilters = () => {
        setPaymentIdFilter("");
        setInvoiceNumberFilter("");
        setRsIdFilter("");
        const defaultStartDate = currentYearStart();
        setStartDateFilter(defaultStartDate);
        setEndDateFilter("");
        setSortBy("payment_created_date");
        setSortDirection("DESC");
        setOnlyMissingReports(false);
        loadPayments({
            page: 0,
            paymentId: "",
            invoiceNumber: "",
            rsId: "",
            startDate: defaultStartDate,
            endDate: "",
            sortBy: "payment_created_date",
            sortDirection: "DESC",
            onlyMissingReports: false
        });
    };

    const pages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
                <h1>GCI Payment Reports</h1>
            </GridItem>

            <GridItem xs={12} sm={12} md={12}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                    <div style={{ width: 160 }}>
                        <TextField
                            label="Payment ID"
                            value={paymentIdFilter}
                            onChange={event => setPaymentIdFilter(stripNonDigits(event.target.value))}
                            fullWidth
                        />
                    </div>
                    <div style={{ width: 180 }}>
                        <TextField
                            label="Invoice #"
                            value={invoiceNumberFilter}
                            onChange={event => setInvoiceNumberFilter(event.target.value)}
                            fullWidth
                        />
                    </div>
                    <div style={{ width: 140 }}>
                        <TextField
                            label="RS ID"
                            value={rsIdFilter}
                            onChange={event => setRsIdFilter(stripNonDigits(event.target.value))}
                            fullWidth
                        />
                    </div>
                    <div style={{ width: 170 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDateFilter}
                            onChange={event => setStartDateFilter(event.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                    <div style={{ width: 170 }}>
                        <TextField
                            label="End Date"
                            type="date"
                            value={endDateFilter}
                            onChange={event => setEndDateFilter(event.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                    <div style={{ width: 170 }}>
                        <label style={{ display: "block", fontSize: 12, color: "#777", marginBottom: 4 }}>
                            Sort By
                        </label>
                        <select
                            value={sortBy}
                            onChange={event => {
                                setSortBy(event.target.value);
                                loadPayments({ page: 0, sortBy: event.target.value });
                            }}
                            style={{
                                width: "100%",
                                height: 38,
                                border: "1px solid #ddd",
                                borderRadius: 4,
                                background: "#fff",
                                padding: "0 10px"
                            }}
                        >
                            <option value="payment_created_date">Created Date</option>
                            <option value="normalized_payment_date">Payment Date</option>
                        </select>
                    </div>
                    <div style={{ width: 170 }}>
                        <label style={{ display: "block", fontSize: 12, color: "#777", marginBottom: 4 }}>
                            Direction
                        </label>
                        <select
                            value={sortDirection}
                            onChange={event => {
                                setSortDirection(event.target.value);
                                loadPayments({ page: 0, sortDirection: event.target.value });
                            }}
                            style={{
                                width: "100%",
                                height: 38,
                                border: "1px solid #ddd",
                                borderRadius: 4,
                                background: "#fff",
                                padding: "0 10px"
                            }}
                        >
                            <option value="DESC">Newest First</option>
                            <option value="ASC">Oldest First</option>
                        </select>
                    </div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={onlyMissingReports}
                                onChange={event => {
                                    setOnlyMissingReports(event.target.checked);
                                    loadPayments({ page: 0, onlyMissingReports: event.target.checked });
                                }}
                                color="primary"
                            />
                        }
                        label="Missing Reports Only"
                    />
                    <Button color="primary" onClick={() => loadPayments({ page: 0 })}>
                        Filter
                    </Button>
                    <Button onClick={resetFilters}>
                        Clear
                    </Button>
                </div>
            </GridItem>

            {(loading || Boolean(workingPaymentId)) && (
                <GridItem xs={12} sm={12} md={12}>
                    <div
                        style={{
                            position: "sticky",
                            top: 10,
                            zIndex: 5,
                            margin: "0 0 16px",
                            padding: "16px 22px",
                            border: "1px solid #e5e5e5",
                            borderLeft: "5px solid #e53935",
                            borderRadius: 6,
                            background: "#fff",
                            boxShadow: "0 4px 18px rgba(0,0,0,0.12)"
                        }}
                    >
                        <LoadingIndicator label={workingMessage || "Loading payments"} large />
                    </div>
                </GridItem>
            )}

            <GridItem xs={12} sm={12} md={12}>
                <ReactTable
                    manual
                    sortable={false}
                    filterable={false}
                    loading={loading || Boolean(workingPaymentId)}
                    loadingText={workingMessage || "Loading payments"}
                    LoadingComponent={TableLoading}
                    className="-striped -highlight"
                    showPaginationTop
                    showPaginationBottom
                    page={page}
                    pages={pages}
                    pageSize={pageSize}
                    pageSizeOptions={[10, 20, 30, 50, 100]}
                    onPageChange={nextPage => loadPayments({ page: nextPage })}
                    onPageSizeChange={(nextPageSize, nextPage) => loadPayments({ page: nextPage, pageSize: nextPageSize })}
                    data={payments.map(payment => ({
                        payment_id: payment.id,
                        payment_created_date: payment.payment_created_date ? formatUTCtoPST(payment.payment_created_date) : "",
                        normalized_payment_date: dateOnly(payment.normalized_payment_date || payment.payment_received_date || payment.deposit_date || payment.payment_date),
                        payment_amount: money(payment.payment_amount),
                        invoice_number: payment.invoice_num || "",
                        invoice_id: payment.invoice_id || "",
                        rs_id: payment.rs_id || "",
                        project_name: payment.project_name || "",
                        active: Number(payment.active) === 1 ? "Active" : "Voided",
                        gci_status: payment.gci_status || "",
                        report_count: Number(payment.report_count || 0),
                        latest_report_at: payment.latest_report_at ? formatUTCtoPST(payment.latest_report_at) : "",
                        actions: (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Tooltip title="Open latest saved report">
                                    <span>
                                        <IconButton
                                            disabled={!payment.latest_report_id || workingPaymentId === payment.id}
                                            onClick={() => openLatestSavedReport(payment)}
                                        >
                                            <Visibility />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={payment.latest_report_id ? "Regenerate report" : "Generate report"}>
                                    <span>
                                        <IconButton
                                            disabled={workingPaymentId === payment.id}
                                            onClick={() => generatePaymentReport(payment)}
                                        >
                                            {workingPaymentId === payment.id ? <Refresh style={{ animation: "gci-loading-spin 0.9s linear infinite" }} /> : (payment.latest_report_id ? <Refresh /> : <Description />)}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </div>
                        )
                    }))}
                    columns={[
                        { Header: "Payment ID", accessor: "payment_id", maxWidth: 110 },
                        { Header: "Created Date", accessor: "payment_created_date", maxWidth: 160 },
                        { Header: "Payment Date", accessor: "normalized_payment_date", maxWidth: 140 },
                        { Header: "Amount", accessor: "payment_amount", maxWidth: 120 },
                        { Header: "Invoice #", accessor: "invoice_number", maxWidth: 120 },
                        { Header: "Invoice ID", accessor: "invoice_id", maxWidth: 110 },
                        { Header: "RS ID", accessor: "rs_id", maxWidth: 90 },
                        { Header: "Project", accessor: "project_name" },
                        { Header: "Payment", accessor: "active", maxWidth: 100 },
                        { Header: "GCI Status", accessor: "gci_status", maxWidth: 120 },
                        { Header: "Reports", accessor: "report_count", maxWidth: 90 },
                        { Header: "Latest Report", accessor: "latest_report_at", maxWidth: 170 },
                        { Header: "Actions", accessor: "actions", sortable: false, maxWidth: 120 }
                    ]}
                />
            </GridItem>

            {pdfData && (
                <CustomPDFViewer
                    pdfData={pdfData}
                    open={true}
                    close={() => setPdfData(null)}
                />
            )}
        </GridContainer>
    );
};

export default GCIPaymentReports;
